import type { FC} from 'react';
import { useCallback, useEffect, useState } from 'react';
import { hasCommunityRoles } from '@/utils/has-roles';
import { toTitleCase } from '@/utils/to-title-case';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Unstable_Grid2 as Grid,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { has } from 'lodash';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';

import type { Meeting } from '@/types/v2/meeting';
import type { Quote } from '@/types/v2/quote';
import { QuotePaymentType } from '@/types/v2/quote';
import { config } from '@/config';
import { useUser } from '@/hooks/v2/use-user';
import ProgressButton from '@/components/common/buttons/progress-button';
import SubLayoutContainer from '@/components/sub-layout-container';

interface ReviewQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  quote: Quote | null;
}

const ReviewQuoteDialog: FC<ReviewQuoteDialogProps> = (props) => {
  const { open, onClose, onRefresh, quote } = props;
  const [meetings, setMeetings] = useState<Meeting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  const formik = useFormik({
    initialValues: {
      id: quote?.id,
      is_approved: Boolean(quote?.is_accepted),
      payment_type: quote?.payment_type ?? '',
      rejection_reason: quote?.rejection_reason ?? '',
      is_pre_approved: Boolean(quote?.meeting_id),
      meeting_id: quote?.meeting_id ?? '',
    },
    validationSchema: Yup.object({
      id: Yup.string().required(),
      is_approved: Yup.boolean(),
      payment_type: Yup.string()
        .min(1)
        .when('is_approved', {
          is: true,
          then: (schema) => schema.required('Payment type is required'),
        }),
      rejection_reason: Yup.string().when('is_approved', {
        is: false,
        then: (schema) => schema.required('Rejection reason is required'),
      }),
      is_pre_approved: Yup.boolean(),
      meeting_id: Yup.string()
        .nullable()
        .min(1)
        .when('is_pre_approved', {
          is: true,
          then: (schema) => schema.required('Meeting is required'),
          otherwise: (schema) => schema.notRequired(),
        })
        .when('is_approved', {
          is: false,
          then: (schema) => schema.notRequired(),
        }),
    }),
    onSubmit: async (values) => {
      if (!quote?.id) return;

      try {
        axios.defaults.withCredentials = true;
        await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
        const response = await axios.patch(
          `${config.site.api}/api/v1/requests/${quote.request_id}/quotes/${quote.id}/review`,
          values,
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );

        const data = response.data;
        if (has(data, 'error')) {
          toast.error(data.error?.message);
          setError(data.error?.message);
          return;
        }

        setError(null);
        toast.success(data.message);
        handleClose();
        onRefresh?.();
      } catch (error) {
        console.error(error?.message);
        toast.error('Something went wrong');
        setError('Something went wrong');
      }
    },
  });

  const handleClose = useCallback((): void => {
    onClose();
    formik.resetForm();
  }, [onClose, formik]);

  useEffect(() => {
    if (hasCommunityRoles(user, ['community_member'])) return;

    const getMeetings = async () => {
      const response = await axios.get(`${config.site.api}/api/v1/meetings`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      const data = response.data;
      if (has(data, 'error')) {
        toast.error(data.error?.message);
        setMeetings([]);
        return;
      }

      setMeetings(response.data);
    };

    getMeetings();
  }, [quote?.id]);

  useEffect(() => {
    formik.resetForm();
    if (quote) {
      formik.setValues({
        id: quote?.id,
        is_approved: Boolean(quote?.is_accepted),
        payment_type: quote?.payment_type ?? '',
        rejection_reason: quote?.rejection_reason ?? '',
        is_pre_approved: Boolean(quote?.meeting_id),
        meeting_id: quote?.meeting_id ?? '',
      });
    }
  }, [quote?.id]);

  useEffect(() => {
    if (formik.values.is_approved) {
      formik.setFieldValue('rejection_reason', '');
    } else {
      formik.setFieldValue('payment_type', '');
      formik.setFieldValue('is_pre_approved', false);
      formik.setFieldValue('meeting_id', '');
    }
  }, [formik.values.is_approved]);

  useEffect(() => {
    if (!formik.values.is_pre_approved) {
      formik.setFieldValue('meeting_id', '');
    }
  }, [formik.values.is_pre_approved]);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={open}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle variant="h5">Review Quote</DialogTitle>
        <DialogContent>
          <SubLayoutContainer>
            <Grid xs={6}>
              <FormControlLabel
                checked={formik.values.is_approved}
                control={<Switch />}
                label="Approve"
                name="is_approved"
                onChange={formik.handleChange}
                value={formik.values.is_approved}
              />
            </Grid>
            {formik.values.is_approved ? (
              <Grid xs={6}>
                <TextField
                  error={formik.touched.payment_type ? Boolean(formik.errors.payment_type) : null}
                  fullWidth
                  helperText={formik.touched.payment_type ? formik.errors.payment_type : null}
                  label="Payment Type"
                  name="payment_type"
                  onChange={formik.handleChange}
                  required={formik.values.is_approved}
                  select
                  value={formik.values.payment_type ?? ''}
                >
                  {Object.values(QuotePaymentType).map((type: string) => (
                    <MenuItem key={type} selected={formik.values.payment_type === type} value={type}>
                      {toTitleCase(type)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}
            {!formik.values.is_approved && (
              <Grid xs={12}>
                <TextField
                  error={formik.touched.rejection_reason ? Boolean(formik.errors.rejection_reason) : null}
                  fullWidth
                  helperText={formik.touched.rejection_reason ? formik.errors.rejection_reason : null}
                  id="rejection_reason"
                  label="Rejection Reason"
                  margin="dense"
                  multiline
                  name="rejection_reason"
                  onChange={formik.handleChange}
                  required={!formik.values.is_approved}
                  rows={3}
                  value={formik.values.rejection_reason}
                />
              </Grid>
            )}
            {formik.values.is_approved ? (
              <Grid xs={12}>
                <FormControlLabel
                  checked={formik.values.is_pre_approved}
                  control={<Switch />}
                  label="Is Pre-Approved"
                  name="is_pre_approved"
                  onChange={formik.handleChange}
                  value={formik.values.is_pre_approved}
                />
              </Grid>
            ) : null}
            {formik.values.is_approved && formik.values.is_pre_approved ? (
              <Grid xs={12}>
                <TextField
                  error={formik.touched.meeting_id ? Boolean(formik.errors.meeting_id) : null}
                  fullWidth
                  helperText={formik.touched.meeting_id ? formik.errors.meeting_id : null}
                  label="Meeting"
                  name="meeting_id"
                  onChange={formik.handleChange}
                  required={formik.values.is_approved ? formik.values.is_pre_approved : null}
                  select
                  value={formik.values.meeting_id}
                >
                  {meetings?.map((meeting: Meeting) => (
                    <MenuItem key={meeting.id} selected={formik.values.meeting_id === meeting.id} value={meeting.id}>
                      {`${toTitleCase(meeting.meeting_type?.name)} - ${dayjs(
                        meeting.held_on ?? meeting.updated_at
                      ).format('DD/MM/YYYY')}`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ) : null}
          </SubLayoutContainer>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <ProgressButton
            disabled={formik.isSubmitting}
            loading={formik.isSubmitting}
            loadingText="Saving..."
            size="medium"
            text="Save"
            type="submit"
            variant="text"
          />
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ReviewQuoteDialog;
